import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const DJ_KNOWAN_LOGO = "https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/ufro47qn_DJKNOWAN%20LOGO%20WHITE.png";

// Project data with logos and details
const projects = [
  {
    id: 'momo',
    title: 'Momo',
    logo: 'https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/5rv3u88e_MOMO%20Logo%20Intro%20TS.png',
    description: 'A mindfulness and emotional wellbeing character world designed for children. Momo helps kids navigate big feelings through gentle storytelling and calming soundscapes.',
    theme: 'Mindfulness & Emotional Wellbeing',
    status: 'Original IP',
    statusColor: 'bg-[#FBBF24]',
  },
  {
    id: 'cosmix',
    title: 'Cosmix Girlz',
    logo: 'https://customer-assets.emergentagent.com/job_461f173b-2f7b-4524-a861-72bba2ed6414/artifacts/cm6617ct_Cosmix%20Logo.png',
    description: 'A vibrant girl-power universe featuring cosmic heroines. Designed for collectibles, fashion dolls, and animated content with empowering themes.',
    theme: 'Empowerment & Adventure',
    status: 'Music by Soundmoo',
    statusColor: 'bg-[#19A4FB]',
  },
  {
    id: 'retrokid',
    title: 'The Retro Kid',
    logo: 'https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/r5fwqxw8_TRK%20BADGE.png',
    description: 'A nostalgic character brand blending retro aesthetics with modern play patterns. Perfect for apparel, collectibles, and lifestyle products.',
    theme: 'Nostalgia & Lifestyle',
    status: 'Original IP',
    statusColor: 'bg-[#FBBF24]',
  },
  {
    id: 'dirtyrobots',
    title: 'Dirty Robots Junkyard Club',
    logo: 'https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/juxj7czx_Dirty%20Rpbots.png',
    description: 'A quirky robot universe where misfit machines find friendship and adventure in the junkyard. Perfect for action figures, games, and animated series.',
    theme: 'Adventure & Friendship',
    status: 'Original IP',
    statusColor: 'bg-[#FBBF24]',
  },
  {
    id: 'suitedbooted',
    title: 'Suited & Rebooted',
    logo: 'https://customer-assets.emergentagent.com/job_c9ad24c8-9a40-422b-823a-575f7c88504d/artifacts/fw7lq1dq_Dirty%20Rpbotssreboot%20white.png',
    description: 'The premium spinoff of Junkyard Club - robots transformed into stylish characters. Ideal for fashion collaborations, premium collectibles, and lifestyle branding.',
    theme: 'Style & Transformation',
    status: 'Original IP',
    statusColor: 'bg-[#FBBF24]',
  },
  {
    id: 'djknowan',
    title: 'DJ KnoWan',
    logo: DJ_KNOWAN_LOGO,
    description: 'A tech house mascot representing the Asian dance music market, blending Soundmoo character identity with global club energy.',
    theme: 'Music & Club Culture',
    status: 'Original IP',
    statusColor: 'bg-[#FBBF24]',
  },
];

// Individual card in the 3D ring
const CarouselCard = ({ project, index, rotation, totalItems, onSelect, isDragging }) => {
  const anglePerItem = 360 / totalItems;
  const baseAngle = index * anglePerItem;
  const radius = 280;

  // Calculate initial transform
  const getTransform = (r) => {
    const currentAngle = baseAngle + r;
    const radians = (currentAngle * Math.PI) / 180;
    const x = Math.sin(radians) * radius;
    const z = Math.cos(radians);
    const normalizedZ = (z + 1) / 2;
    return {
      x,
      scale: 0.55 + normalizedZ * 0.45,
      opacity: 0.3 + normalizedZ * 0.7,
      zIndex: Math.round(normalizedZ * 10),
    };
  };

  const [transform, setTransform] = useState(() => getTransform(0));

  useEffect(() => {
    // Set initial transform
    setTransform(getTransform(rotation.get()));
    
    const unsubscribe = rotation.on('change', (r) => {
      setTransform(getTransform(r));
    });
    
    return () => unsubscribe();
  }, [rotation, baseAngle, radius]);

  return (
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-y-1/2 cursor-pointer"
      style={{
        x: transform.x - 140,
        scale: transform.scale,
        opacity: transform.opacity,
        zIndex: transform.zIndex,
      }}
      onClick={() => !isDragging && onSelect(index)}
    >
      <motion.div
        className="relative w-[280px] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)] bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border border-white/20"
        whileHover={{ scale: 1.03 }}
        transition={{ duration: 0.2 }}
      >
        {/* Status Badge - Top Center */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
          <span className={`px-3 py-1 rounded-full text-[10px] font-bold text-[#0A1A3F] ${project.statusColor}`}>
            {project.status}
          </span>
        </div>
        
        {/* Logo/Artwork */}
        <div className="p-6 pt-10 flex items-center justify-center h-[220px]">
          <img
            src={project.logo}
            alt={project.title}
            className="max-h-full max-w-full object-contain drop-shadow-lg"
            draggable={false}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export const CharacterShowcase = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  
  const rotation = useMotionValue(0);
  const lastX = useRef(0);
  const velocity = useRef(0);
  const animationRef = useRef(null);
  
  const totalItems = projects.length;
  const anglePerItem = 360 / totalItems;

  // Track which card is in front based on rotation
  useEffect(() => {
    const updateSelectedIndex = (r) => {
      // Card positions:
      // - Index 0 (Momo): baseAngle = 0
      // - Index 1 (Cosmix): baseAngle = 120
      // - Index 2 (Retro Kid): baseAngle = 240
      // 
      // Card is in front when cos(baseAngle + r) = 1 (angle = 0)
      // So baseAngle + r = 0 means that card is in front
      // r = -baseAngle = -index * anglePerItem
      //
      // Given r, find index: index = -r / anglePerItem
      
      // Normalize r to [0, 360)
      let normalizedR = r % 360;
      if (normalizedR < 0) normalizedR += 360;
      
      // Since card i is front when r = -i * anglePerItem
      // Equivalently: i = (360 - normalizedR) / anglePerItem when normalizedR > 0
      // Or more simply: i = round((360 - normalizedR) / anglePerItem) % totalItems
      
      const rawIndex = (360 - normalizedR) / anglePerItem;
      const index = Math.round(rawIndex) % totalItems;
      
      setSelectedIndex(index);
    };
    
    const unsubscribe = rotation.on('change', updateSelectedIndex);
    updateSelectedIndex(0); // Initial call
    return () => unsubscribe();
  }, [anglePerItem, totalItems, rotation]);

  // Momentum physics - natural slow down like a DJ deck
  const startMomentum = (initialVelocity) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const friction = 0.95; // Friction coefficient
    const minVelocity = 0.1;
    
    let vel = initialVelocity;
    let rot = rotation.get();
    
    const tick = () => {
      vel *= friction;
      rot += vel;
      rotation.set(rot);
      
      if (Math.abs(vel) > minVelocity) {
        animationRef.current = requestAnimationFrame(tick);
      } else {
        // Snap to nearest card
        const normalizedR = ((rot % 360) + 360) % 360;
        const nearestIndex = Math.round(-normalizedR / anglePerItem + totalItems) % totalItems;
        const targetRotation = -nearestIndex * anglePerItem;
        
        // Find shortest path to target
        const current = rot % 360;
        let diff = (targetRotation % 360) - current;
        if (diff > 180) diff -= 360;
        if (diff < -180) diff += 360;
        
        animate(rotation, rot + diff, {
          type: 'spring',
          stiffness: 150,
          damping: 20,
        });
      }
    };
    
    animationRef.current = requestAnimationFrame(tick);
  };

  const handlePointerDown = (e) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setIsDragging(true);
    lastX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    velocity.current = 0;
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const deltaX = clientX - lastX.current;
    
    // Update velocity for momentum
    velocity.current = deltaX * 0.5;
    
    // Update rotation
    const newRotation = rotation.get() + deltaX * 0.3;
    rotation.set(newRotation);
    
    lastX.current = clientX;
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    startMomentum(velocity.current);
  };

  const selectProject = (index) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const targetRotation = -index * anglePerItem;
    const currentRotation = rotation.get();
    
    // Find shortest path
    const current = currentRotation % 360;
    let diff = (targetRotation % 360) - current;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    
    animate(rotation, currentRotation + diff, {
      type: 'spring',
      stiffness: 100,
      damping: 18,
    });
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const selectedProject = projects[selectedIndex];

  return (
    <section
      id="characters"
      data-testid="characters-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="soundmoo-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-2"
        >
          <motion.span className="text-sm font-medium tracking-widest uppercase text-white/60 mb-4 block">
            Our Work
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-3">
            Originals & Partner Projects
          </h2>
          <p className="text-base text-white/70 max-w-xl mx-auto">
            From original character worlds to collaborative brand partnerships.
          </p>
        </motion.div>

        {/* Drag hint - ABOVE cards */}
        <div className="text-center text-white/40 text-sm mb-2">
          Drag to spin • Click to explore
        </div>

        {/* 3D Ring Carousel */}
        <div
          ref={containerRef}
          className="relative h-[280px] flex items-start justify-center cursor-grab active:cursor-grabbing select-none overflow-visible"
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          style={{ perspective: '1000px' }}
        >
          {projects.map((project, index) => (
            <CarouselCard
              key={project.id}
              project={project}
              index={index}
              rotation={rotation}
              totalItems={totalItems}
              onSelect={selectProject}
              isDragging={isDragging}
            />
          ))}
        </div>

        {/* Selected Project Info - Below Cards */}
        <div className="mt-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] mb-4">
              {selectedProject.title}
            </h3>
            <p className="text-white/80 leading-relaxed mb-6 max-w-lg mx-auto">
              {selectedProject.description}
            </p>
            <motion.button
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white font-medium hover:bg-white/10 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
            >
              Explore Concept
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </AnimatePresence>
        </div>

        {/* Dot Navigation */}
        <div className="flex justify-center gap-2 mt-8">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => selectProject(index)}
              className={`transition-all duration-300 rounded-full ${
                index === selectedIndex
                  ? 'w-6 h-2 bg-white'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
